import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Briefcase, ListChecks, FileText, MapPin, Snowflake, Camera, Plus, Navigation, PenLine, Check } from "lucide-react";
import { DashboardShell } from "@/components/DashboardShell";
import { PhotoUpload } from "@/components/PhotoUpload";
import { SignaturePad } from "@/components/SignaturePad";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import api, { apiError, API } from "@/lib/api";

const STATUS = { claimed: "Acceptée", in_progress: "En cours", completed: "Terminée" };

export default function IntervenantDashboard() {
  const [tab, setTab] = useState("disponibles");
  const qc = useQueryClient();
  const { data: available = [] } = useQuery({ queryKey: ["missions-available"], queryFn: async () => (await api.get("/missions/available")).data, refetchInterval: 10000 });
  const { data: mine = [] } = useQuery({ queryKey: ["missions-mine"], queryFn: async () => (await api.get("/missions/mine")).data });

  const nav = [
    { key: "disponibles", label: "Missions", icon: Briefcase, onClick: () => setTab("disponibles") },
    { key: "mes-missions", label: "Mes RDV", icon: ListChecks, onClick: () => setTab("mes-missions") },
    { key: "contrat", label: "Contrat", icon: FileText, onClick: () => setTab("contrat") },
  ];

  const claim = async (id) => {
    try { await api.post(`/missions/${id}/claim`); toast.success("Mission acceptée !"); qc.invalidateQueries({ queryKey: ["missions-available"] }); qc.invalidateQueries({ queryKey: ["missions-mine"] }); setTab("mes-missions"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <DashboardShell title="Espace Technicien d'entretien" nav={nav} active={tab} accent="#10B981">
      {tab === "disponibles" && (
        <div>
          <h2 className="mb-1 text-lg font-bold text-[#263871]">Missions disponibles</h2>
          <p className="mb-5 text-sm text-slate-500">Premier arrivé, premier servi. Vous percevez le montant net (marge plateforme : 10€/clim).</p>
          {available.length === 0 ? <Empty text="Aucune mission disponible pour le moment." /> : (
            <div className="grid gap-4 sm:grid-cols-2">
              {available.map((m) => (
                <div key={m.id} data-testid={`available-mission-${m.id}`} className="rounded-2xl border border-slate-200 bg-white p-5">
                  <div className="flex items-center justify-between">
                    <h3 className="flex items-center gap-2 font-bold text-[#263871]"><Snowflake className="h-4 w-4 text-[#1D9BF0]" />{m.nb_clim} clim · {m.service_type}</h3>
                    <Badge className="bg-emerald-100 text-emerald-700">{m.technician_payout}€ net</Badge>
                  </div>
                  <p className="mt-2 flex items-center gap-1 text-sm text-slate-600"><MapPin className="h-3.5 w-3.5" />{m.address}</p>
                  <p className="text-xs text-slate-400">{m.scheduled_date} · {m.slot}</p>
                  <Button data-testid={`claim-${m.id}`} onClick={() => claim(m.id)} className="mt-4 w-full rounded-full bg-emerald-600 hover:bg-emerald-700">Accepter la mission</Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {tab === "mes-missions" && <MyMissions mine={mine} qc={qc} />}
      {tab === "contrat" && <Contract />}
    </DashboardShell>
  );
}

function MyMissions({ mine, qc }) {
  const upload = async (id, phase, paths) => {
    try { await api.post(`/missions/${id}/photos`, { phase, paths }); toast.success(phase === "before" ? "Photos AVANT enregistrées" : "Mission terminée !"); qc.invalidateQueries({ queryKey: ["missions-mine"] }); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div>
      <h2 className="mb-5 text-lg font-bold text-[#263871]">Mes interventions</h2>
      {mine.length === 0 ? <Empty text="Vous n'avez pas encore accepté de mission." /> : (
        <div className="space-y-4">
          {mine.map((m) => (
            <div key={m.id} data-testid={`my-mission-${m.id}`} className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-[#263871]">{m.client_name} — {m.nb_clim} clim</h3>
                <Badge variant="outline">{STATUS[m.status] || m.status}</Badge>
              </div>
              <p className="mt-1 flex items-center gap-1 text-sm text-slate-600"><MapPin className="h-3.5 w-3.5" />{m.address}</p>
              <p className="text-xs text-slate-400">{m.scheduled_date} · {m.scheduled_time || m.slot} · {m.duration_minutes ? `~${m.duration_minutes}min · ` : ""}{m.technician_payout}€ net{m.perfume ? ` · Parfum: ${m.perfume}` : ""}</p>
              <a
                data-testid={`waze-${m.id}`}
                href={m.address_lat && m.address_lon ? `https://waze.com/ul?ll=${m.address_lat},${m.address_lon}&navigate=yes` : `https://waze.com/ul?q=${encodeURIComponent(m.address)}&navigate=yes`}
                target="_blank" rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#33CCFF] px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
              >
                <Navigation className="h-4 w-4" />Itinéraire Waze
              </a>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <PhotoUpload value={m.photos_before || []} onChange={(paths) => upload(m.id, "before", paths.filter((p) => !(m.photos_before || []).includes(p)))} label="Photos AVANT" testid={`before-${m.id}`} />
                <PhotoUpload value={m.photos_after || []} onChange={(paths) => upload(m.id, "after", paths.filter((p) => !(m.photos_after || []).includes(p)))} label="Photos APRÈS (clôture)" testid={`after-${m.id}`} />
              </div>
              <LeadForm missionAddress={m.address} clientName={m.client_name} />
              {m.status === "completed" && <SignMission mission={m} qc={qc} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SignMission({ mission, qc }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(mission.client_name || "");
  const sign = async (dataUrl) => {
    try {
      await api.post(`/missions/${mission.id}/sign`, { signature: dataUrl, signer_name: name });
      toast.success("Bon d'intervention signé !");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["missions-mine"] });
    } catch (e) { toast.error(apiError(e)); }
  };
  if (mission.signature) {
    return (
      <div className="mt-4 flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">
        <Check className="h-4 w-4" />Bon d'intervention signé{mission.signer_name ? ` par ${mission.signer_name}` : ""}
      </div>
    );
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`sign-${mission.id}`} className="mt-4 w-full rounded-full bg-[#263871] hover:bg-[#35508F]"><PenLine className="mr-2 h-4 w-4" />Faire signer le bon d'intervention</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Bon d'intervention — signature client</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="rounded-xl bg-slate-50 p-3 text-sm text-slate-600">{mission.nb_clim} clim · {mission.service_type} · {mission.address}</div>
          <div><Label>Nom du signataire</Label><Input data-testid={`signer-name-${mission.id}`} value={name} onChange={(e) => setName(e.target.value)} className="mt-1" /></div>
          <Label>Signature du client</Label>
          <SignaturePad onSave={sign} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LeadForm({ missionAddress, clientName }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_name: clientName, client_phone: "", address: missionAddress, nb_clim: 1, details: "" });
  const set = (k, v) => setForm({ ...form, [k]: v });
  const submit = async () => {
    if (!form.client_phone) return toast.error("Téléphone client requis");
    try { await api.post("/leads", { ...form, type: "installation", nb_clim: Number(form.nb_clim) }); toast.success("Lead d'installation envoyé à la gare de triage !"); setOpen(false); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="submit-lead-btn" variant="outline" className="mt-4 rounded-full border-2"><Plus className="mr-1 h-4 w-4" />Le client veut installer une clim</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Nouveau lead installation</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nom du client</Label><Input data-testid="lead-name" value={form.client_name} onChange={(e) => set("client_name", e.target.value)} className="mt-1" /></div>
          <div><Label>Téléphone</Label><Input data-testid="lead-phone" value={form.client_phone} onChange={(e) => set("client_phone", e.target.value)} className="mt-1" /></div>
          <div><Label>Adresse</Label><Input data-testid="lead-address" value={form.address} onChange={(e) => set("address", e.target.value)} className="mt-1" /></div>
          <div><Label>Nb de clims à poser</Label><Input data-testid="lead-nb" type="number" min={1} value={form.nb_clim} onChange={(e) => set("nb_clim", e.target.value)} className="mt-1" /></div>
          <Textarea data-testid="lead-details" placeholder="Détails du projet" value={form.details} onChange={(e) => set("details", e.target.value)} />
          <Button data-testid="confirm-lead" onClick={submit} className="w-full rounded-full bg-[#263871]">Envoyer le lead</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Contract() {
  return (
    <div className="mx-auto max-w-2xl rounded-2xl border border-slate-200 bg-white p-8">
      <h2 className="text-xl font-bold text-[#263871]">Contrat de sous-traitance</h2>
      <p className="mt-2 text-sm text-slate-500">Climateasy &times; Intervenant indépendant</p>
      <div className="mt-6 space-y-4 text-sm leading-relaxed text-slate-700">
        <p><strong>Article 1 — Objet.</strong> L'intervenant réalise des prestations d'entretien et de nettoyage de climatisation pour le compte de la plateforme Climateasy auprès des clients qu'elle lui transmet.</p>
        <p><strong>Article 2 — Rémunération.</strong> Pour chaque clim murale entretenue pour un particulier, l'intervenant perçoit 50€ HT (60€ HT facturés au client, dont 10€ de marge plateforme par clim).</p>
        <div className="rounded-xl border-2 border-[#1D9BF0] bg-[#E0F2FE] p-4">
          <p><strong>Article 3 — Clause de non-concurrence.</strong> Le client reste la propriété exclusive de la plateforme Climateasy. L'intervenant s'interdit de démarcher, contacter ou récupérer directement tout client de la plateforme pendant une durée de <strong>5 ans</strong> à compter de la date de sa dernière collaboration avec Climateasy.</p>
        </div>
        <p><strong>Article 4 — Preuve d'intervention.</strong> L'intervenant s'engage à fournir une photo avant et une photo après chaque intervention, comme preuve de remise en état et de propreté.</p>
      </div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center text-slate-500">{text}</div>;
}
